import { useActions, useValues } from 'kea'
import React, { useEffect, useState } from 'react'
import {
    PROPERTY_MANAGEMENT_CLOUD_MINIMUM_PRICING,
    PROPERTY_MANAGEMENT_CLOUD_ENTERPRISE_MINIMUM_PRICING,
    ONPERMISE_ENTERPRISE_MINIMUM_PRICING,
    ONPERMISE_PROFESSIONAL_MINIMUM_PRICING,
    pricing,
    pricingLabels,
} from '../constants'
import { PricingSlider } from '../PricingSlider'
import { pricingSliderLogic } from '../PricingSlider/pricingSliderLogic'
import { motion } from 'framer-motion'
import Link from 'components/Link'
import Toggle from 'components/Toggle'
import { Info } from 'components/Icons/Icons'
import useiCloudReady from '../../../hooks/useiCloudReady'

interface IPricingOptions {
    minimumPrice: number
    title: string
    subtitle: string
    badge: string
    breakdown?: number[]
    crmBreakdown?: number[]
    icon: React.ReactNode
    mainCTA: {
        title: string
        url: string
    }
    demoCTA?: {
        title: string
        url: string
    }
}

interface IconProps {
    className?: string
}

const B2C = () => {
    return (
        <div className="col-span-3 mt-2 border border-dashed border-gray-accent bg-black bg-opacity-[2%] py-2 px-4 text-center">
            B2C company with insane event volume?{' '}
            <Link to="/signup/b2c" className="inline-block font-bold text-orange">
                Apply for a discount
            </Link>
        </div>
    )
}
const Breakdown = ({ planName }) => {
    const breakdown = pricing[planName]
    return breakdown.map((price, index) => {
        const label = pricingLabels[price[0]]
        const included = price[1] === 0
        return (
            <>
                <div className="col-span-2">{label || '100 million - 1 billion'}</div>
                <strong className="text-right">
                    {included ? (
                        'Included'
                    ) : (
                        <>
                            {price[1]}
                            <span className="font-normal ">/event</span>
                        </>
                    )}
                </strong>
            </>
        )
    })
}

const Button = ({
    onClick,
    children,
    active,
}: {
    onClick?: () => void
    children: React.ReactNode
    active?: boolean
}) => {
    return (
        <button
            className={`relative flex flex-grow items-center justify-between rounded-sm bg-white px-4 py-2 text-lg font-bold  shadow-sm transition-none hover:text-black/95 active:top-[1px] active:scale-[.97] active:border-[2.5px] active:border-red sm:w-[186px] sm:flex-grow-0 ${
                active ? 'border-red' : 'border-white'
            } border-[2.5px] transition-colors`}
            onClick={onClick}
        >
            <span>{children}</span>

            <svg
                className={`${active ? 'opacity-100' : 'opacity-0'} transition-none`}
                width="18"
                height="14"
                viewBox="0 0 18 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M2.9947 5.52548L6.4635 8.99428L14.7025 0.75528C15.2095 0.24824 16.0369 0.24824 16.5439 0.75528L17.3259 1.53732C17.833 2.04436 17.833 2.8717 17.3259 3.37872L7.46034 13.2443C6.95566 13.749 6.13534 13.7521 5.62674 13.2521L0.389145 8.10213C0.126645 7.84509 -0.00381509 7.52713 8.49096e-05 7.15995C0.00399111 6.79277 0.141491 6.47791 0.408685 6.22635L1.18056 5.49979C1.69306 5.01775 2.49696 5.02947 2.99462 5.52714L2.9947 5.52548Z"
                    fill="#F54E00"
                />
            </svg>
        </button>
    )
}

export const CloudIcon = ({ className = '' }: IconProps) => {
    return (
        <svg
            className={className}
            width="36"
            height="24"
            viewBox="0 0 36 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M31.7322 11.3038C31.5466 6.10751 26.536 2.58203 21.6184 3.97451C16.979 -2.98357 6.12288 -0.107886 5.75316 8.33519C-2.96856 10.7469 -1.2978 23.9236 7.88785 23.9236H29.5991C36.9283 23.9236 38.5062 13.5314 31.7338 11.3052L31.7322 11.3038Z"
                fill="black"
            />
        </svg>
    )
}

export const OnPremiseIcon = ({ className = '' }: IconProps) => {
    return (
        <svg
            className={className}
            width="32"
            height="26"
            viewBox="0 0 32 26"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M32 5C32 2.23875 29.7612 0 27 0H5C2.23875 0 -4.07046e-07 2.23875 -4.07046e-07 5V7C-4.07046e-07 9.76125 2.23875 12 5 12H15V16.0998C13.2025 16.4648 11.7549 17.7936 11.2211 19.5248C11.1499 19.5085 11.0761 19.4998 10.9999 19.4998H0.999869C0.447356 19.4998 -0.000131607 19.9473 -0.000131607 20.4998C-0.000131607 21.0523 0.447356 21.4998 0.999869 21.4998H11.0248C11.2748 24.026 13.4073 25.9996 15.9999 25.9996C18.5924 25.9996 20.725 24.0259 20.9749 21.4998H30.9999C31.5524 21.4998 31.9999 21.0523 31.9999 20.4998C31.9999 19.9473 31.5524 19.4998 30.9999 19.4998H20.9999C20.9236 19.4998 20.8499 19.5085 20.7786 19.5248C20.2449 17.7935 18.7974 16.4648 16.9997 16.0998V12H26.9997C29.761 12 31.9997 9.76125 31.9997 7L32 5ZM4 6C4 4.89501 4.89501 4 6 4C7.10499 4 8 4.89501 8 6C8 7.10499 7.10499 8 6 8C4.89501 8 4 7.10499 4 6ZM12 4C10.895 4 10 4.89501 10 6C10 7.10499 10.895 8 12 8C13.105 8 14 7.10499 14 6C14 4.89501 13.105 4 12 4Z"
                fill="black"
            />
        </svg>
    )
}

const propertymanagementcloudOptions = {
    minimumPrice: PROPERTY_MANAGEMENT_CLOUD_MINIMUM_PRICING,
    title: 'iCloudReady Cloud',
    subtitle: 'Turnkey, hosted by iCloudReady',
    badge: 'Standard',
    icon: <CloudIcon className="w-[36px] opacity-30" />,
    mainCTA: {
        title: 'Get started',
        url: 'https://app.icloud-ready.com/register',
    },
    demoCTA: {
        title: 'Join a group demo',
        url: '/book-a-meeting?plan=cloud&demo=group#demo',
    },
}

const propertymanagementcloudEnterpriseOptions = {
    minimumPrice: PROPERTY_MANAGEMENT_CLOUD_ENTERPRISE_MINIMUM_PRICING,
    title: 'iCloudReady Cloud',
    subtitle: 'Managed & supported by iCloudReady',
    badge: 'Enterprise',
    icon: <CloudIcon className="w-[36px] opacity-30" />,
    mainCTA: {
        title: 'book a meeting',
        url: '/book-a-meeting?plan=professional#contact',
    },
    demoCTA: {
        title: 'Book a demo',
        url: '/book-a-meeting?plan=professional&demo=professional#demo',
    },
}

const propertymanagementcloudEnterpriseOptions2 = {
    minimumPrice: ONPERMISE_ENTERPRISE_MINIMUM_PRICING,
    title: 'iCloudReady',
    subtitle: 'Deploy to your infrastructure or private cloud',
    badge: 'Enterprise',
    icon: <OnPremiseIcon className="w-[36px] opacity-30" />,

    mainCTA: {
        title: 'book a meeting',
        url: '/contact-us?edition=professional',
    },
    demoCTA: {
        title: 'Book a demo',
        url: '/book-a-meeting?plan=professional&demo=professional#demo',
    },
}

const onPremiseOptions = {
    minimumPrice: ONPERMISE_PROFESSIONAL_MINIMUM_PRICING,
    title: 'iCloudReady On-Premise',
    subtitle: 'Deploy to your infrastructure or private cloud',
    badge: 'Standard',
    icon: <OnPremiseIcon className="w-[36px] opacity-30" />,
    mainCTA: {
        title: 'Get started',
        url: 'https://license.icloud-ready.com/',
    },
    demoCTA: {
        title: 'Join a group demo',
        url: '/book-a-meeting?plan=on-permise&demo=group#demo',
    },
}

const onPremiseEnterpriseOptions = {
    minimumPrice: ONPERMISE_ENTERPRISE_MINIMUM_PRICING,
    title: 'iCloudReady',
    subtitle: 'Deploy to your infrastructure or private cloud',
    badge: 'Enterprise',
    icon: <OnPremiseIcon className="w-[36px] opacity-30" />,
    mainCTA: {
        title: 'Get started',
        url: 'https://license.icloud-ready.com/?price_id=price_1L1AeWEuIatRXSdzj0Y5ioOU',
    },
    demoCTA: {
        title: 'Book a demo',
        url: '/book-a-meeting?plan=professional&demo=professional#demo',
    },
}

export default function Calculator({
    onPremise,
    enterprise,
    professionalMode,
    handleProfessionalModeChange,
    setCurrentModal,
}: {
    onPremise: boolean
    enterprise: boolean
    professionalMode: boolean
    handleProfessionalModeChange: (checked: boolean) => void
    setCurrentModal: (currentModal: string) => void
}) {
    const icloudready = useiCloudReady()
    const [showVolumeDiscounts, setShowVolumeDiscounts] = useState(false)
    const { finalMonthlyCost, propertyManagemenSliderValue, pricingOption, crmPricingOption } =
        useValues(pricingSliderLogic)
    const [showSlider, setShowSlider] = useState(false)
    const [optionDetails, setOptionDetails] = useState<IPricingOptions | undefined>(propertymanagementcloudOptions)
    const breakdown = pricing[pricingOption]
    const crmBreakdown = pricing[crmPricingOption]
    const monthlyMinimumPrice =
        optionDetails &&
        optionDetails.minimumPrice.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        })

    const { setPricingOption, setCrmPricingOption, setPropertyManagemenSliderValue, setRealestateCrmSliderValue } =
        useActions(pricingSliderLogic)

    useEffect(() => {
        let optionDetails
        let pricingOption
        let crmPricingOption
        if (onPremise && enterprise) {
            optionDetails = onPremiseEnterpriseOptions
            pricingOption = 'property-management-on-premise-professional'
            crmPricingOption = 'real-estate-crm-on-premise-professional'
        }
        if (!onPremise && enterprise) {
            optionDetails = propertymanagementcloudEnterpriseOptions2
            pricingOption = 'propertymanagementcloud-professional'
            crmPricingOption = 'real-estate-crm-cloud-professional'
        }
        if (!onPremise && !enterprise) {
            optionDetails = propertymanagementcloudOptions
            pricingOption = 'propertymanagementcloud-standard'
            crmPricingOption = 'real-estate-crm-cloud-standard'
        }
        if (onPremise && !enterprise) {
            optionDetails = onPremiseOptions
            pricingOption = 'property-management-on-premise-standard'
            crmPricingOption = 'real-estate-crm-on-premise-standard'
        }
        setPricingOption(pricingOption)
        setCrmPricingOption(crmPricingOption)
        setOptionDetails(optionDetails)
    }, [onPremise, enterprise])

    useEffect(() => {
        setPropertyManagemenSliderValue(4)
        setRealestateCrmSliderValue(3)
    }, [])

    return (
        <motion.div
            initial={{ opacity: 0, translateY: -20 }}
            animate={{ opacity: 1, translateY: 0 }}
            className="w-full bg-transparent"
        >
            <div>
                <h4 className="m-0 text-base font-bold ">Volume discounts for property management</h4>
                {pricingOption && (
                    <>
                        <ul className="m-0 grid gap-y-1 p-0">
                            {breakdown.map((price, index) => {
                                const label = pricingLabels[price[0]]
                                return (
                                    <li
                                        key={index}
                                        className="flex items-center justify-between space-x-2 border-b border-dashed border-gray-accent-light pb-2 opacity-50 last:border-b-0 last:pb-0"
                                    >
                                        <p className="m-0 text-[14px] font-medium">{label || '100 - 1000 unit'}</p>
                                        <p className="m-0 text-[14px] font-medium">
                                            {price[1] === 0
                                                ? professionalMode
                                                    ? '$450 (flat fee)'
                                                    : 'Free'
                                                : `$${price[1]}`}
                                        </p>
                                    </li>
                                )
                            })}
                        </ul>
                    </>
                )}
                {/* <h4 className="text-base font-bold m-0 ">Volume discounts for real estate crm</h4>
                                {crmPricingOption && (
                    <>
                        <ul className="grid gap-y-1 m-0 p-0">
                            {crmBreakdown?.map((price, index) => {
                                const label = pricingLabels[price[0]]
                                return (
                                    <li
                                        key={index}
                                        className="flex items-center space-x-2 justify-between opacity-50 border-b border-dashed border-gray-accent-light pb-2 last:pb-0 last:border-b-0"
                                    >
                                        <p className="text-[14px] font-medium m-0">
                                            {label || '100 - 1000 unit'}
                                        </p>
                                        <p className="text-[14px] font-medium m-0">
                                            {price[1] === 0
                                                ? professionalMode
                                                    ? '$450 (flat fee)'
                                                    : 'Free'
                                                : `$${price[1]}`}
                                        </p>
                                    </li>
                                )
                            })}
                        </ul>
                    </>
                )} */}

                {/* <p className="text-sm pt-2 mt-2 mb-4 pb-0 m-0  border-t border-dashed border-gray-accent-light">
                    * We offer B2C companies up to 30% off. book a meeting after you've started sending data to iCloudReady.
                </p> */}
                <B2C />

                <div className="border-t border-dashed border-gray-accent-light pt-4">
                    <div className="flex items-center justify-between">
                        <div className="flex gap-x-1">
                            <h4 className="m-0 text-base">Enterprise package</h4>
                            <button
                                className="text-sm font-semibold text-black"
                                onClick={() => setCurrentModal('enterprise')}
                            >
                                <Info />
                            </button>
                        </div>
                        <Toggle checked={professionalMode} onChange={handleProfessionalModeChange} />
                    </div>
                    <p className="m-0 mt-1.5 mb-0 text-sm text-black/60">
                        Starts at $450/mo and comes with SSO, advanced permissions, and a dedicated Slack channel for
                        support
                    </p>
                </div>

                <div className="mt-4 border-t border-dashed border-gray-accent-light pt-4">
                    {!showSlider || (
                        // (icloudready?.isFeatureEnabled && !icloudready?.isFeatureEnabled('pricing-page-slider'))) && (
                        <button onClick={() => setShowSlider(true)} className="text-sm font-bold text-red">
                            Calculate your monthly price
                        </button>
                    )}
                    <div
                        style={{
                            display: showSlider || 'block',
                            // (icloudready?.isFeatureEnabled && icloudready?.isFeatureEnabled('pricing-page-slider'))
                            //     ? 'block'
                            //     : 'none',
                        }}
                    >
                        <h4 className="m-0 pb-1 text-base">Calculate your monthly price</h4>
                        <div>
                            <p className="m-0 text-sm font-bold text-black/60">Monthly unit volume</p>
                            <div className="mb-8 mt-3">
                                <PricingSlider marks={[10, 50, 100, 500, 1000]} min={10} max={1000} />
                            </div>
                            <p className="m-0 pb-0 text-sm">
                                <Link className="font-semibold" to="/blog/calculating-events-from-users">
                                    How to estimate your unit volume
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    )
}
